import {
  Alerta,
  Botao,
  CabecalhoCartao,
  Campo,
  Cartao,
  CorpoCartao,
  Entrada,
  EstadoCarregando,
  RodapeCartao,
  Selecao,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import { clientesApi, leadsApi } from '../../api/leads'
import { ErroApi } from '../../api/erros'
import { EstadoDeErro, EstadoIndisponivel } from '../../componentes/estados-tela'
import { IconeVoltar } from '../../componentes/icones'
import { opcoesDeCatalogo, useCatalogoAtivo } from '../../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../../layout/layout-app'
import { apenasDigitos } from '../../utilitarios/formatar'

/**
 * TELA-06 — Editar lead.
 *
 * Edita **apenas dados cadastrais**. Situação, responsável, equipe e unidade não
 * estão aqui de propósito: status muda por interação (RN-22) e responsável/equipe
 * por transferência, que exige motivo (RN-26). Um formulário que salvasse esses
 * campos em silêncio driblaria as duas regras e deixaria a timeline sem o registro.
 *
 * A tela é dividida em dois destinos, porque a API divide: dados do **cliente** vão
 * para `PATCH /clientes/{uuid}`; classificação do **lead** (ponto de conversão,
 * produto, valor) vai para `PATCH /leads/{uuid}`.
 */
export function PaginaEditarLead() {
  const { uuid = '' } = useParams()
  const navegar = useNavigate()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()

  const consulta = useQuery({
    queryKey: chaves.leads.detalhe(uuid),
    queryFn: ({ signal }) => leadsApi.detalhar(uuid, signal),
    enabled: uuid !== '',
  })

  const pontos = useCatalogoAtivo('pontos-conversao')
  const produtos = useCatalogoAtivo('produtos-associados')

  const [cadastro, setCadastro] = useState({
    nome: '',
    email: '',
    documento: '',
    telefoneAlternativo: '',
    whatsapp: '',
    cep: '',
    logradouro: '',
    bairro: '',
    complemento: '',
    cidade: '',
    estado: '',
    cargo: '',
    empresa: '',
    site: '',
  })
  const [classificacao, setClassificacao] = useState({
    pontoConversaoId: '',
    produtoAssociadoId: '',
    valorNegocio: '',
  })
  const [erroCliente, setErroCliente] = useState<ErroApi | null>(null)

  const lead = consulta.data

  useEffect(() => {
    if (!lead) return
    setCadastro({
      nome: lead.cliente.nome,
      email: lead.cliente.email ?? '',
      documento: lead.cliente.documento ?? '',
      telefoneAlternativo: lead.cliente.contatoAlternativo ?? '',
      whatsapp: lead.cliente.whatsapp ?? '',
      cep: lead.cliente.endereco.cep ?? '',
      logradouro: lead.cliente.endereco.logradouro ?? '',
      bairro: lead.cliente.endereco.bairro ?? '',
      complemento: lead.cliente.endereco.complemento ?? '',
      cidade: lead.cliente.endereco.cidade ?? '',
      estado: lead.cliente.endereco.estado ?? '',
      cargo: lead.cliente.profissional.cargo ?? '',
      empresa: lead.cliente.profissional.empresa ?? '',
      site: lead.cliente.profissional.site ?? '',
    })
    setClassificacao({
      pontoConversaoId: lead.pontoConversao ? String(lead.pontoConversao.id) : '',
      produtoAssociadoId: lead.produtoAssociado ? String(lead.produtoAssociado.id) : '',
      valorNegocio: lead.valorNegocio ?? '',
    })
  }, [lead])

  const salvarClassificacao = useMutation({
    mutationFn: () =>
      leadsApi.atualizar(uuid, {
        pontoConversaoId: classificacao.pontoConversaoId
          ? Number(classificacao.pontoConversaoId)
          : null,
        produtoAssociadoId: classificacao.produtoAssociadoId
          ? Number(classificacao.produtoAssociadoId)
          : null,
        valorNegocio: classificacao.valorNegocio.trim() || null,
      }),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.todos })
      avisos.sucesso('Classificação atualizada', 'A alteração entrou na auditoria.')
    },
    onError: (erro) => avisos.erro('Não foi possível salvar', erro.message),
  })

  const salvarCliente = useMutation({
    mutationFn: () =>
      clientesApi.atualizar(lead?.cliente.uuid ?? '', {
        nome: cadastro.nome,
        email: cadastro.email.trim() || null,
        documento: cadastro.documento.trim() ? apenasDigitos(cadastro.documento) : null,
        endereco: {
          cep: cadastro.cep.trim() || null,
          logradouro: cadastro.logradouro.trim() || null,
          bairro: cadastro.bairro.trim() || null,
          complemento: cadastro.complemento.trim() || null,
          cidade: cadastro.cidade.trim() || null,
          estado: cadastro.estado.trim() || null,
        },
        profissional: {
          cargo: cadastro.cargo.trim() || null,
          empresa: cadastro.empresa.trim() || null,
          site: cadastro.site.trim() || null,
        },
      }),
    onSuccess: () => {
      setErroCliente(null)
      void clienteConsulta.invalidateQueries({ queryKey: chaves.leads.detalhe(uuid) })
      avisos.sucesso('Cadastro do cliente atualizado')
    },
    onError: (erro) => {
      if (erro instanceof ErroApi) setErroCliente(erro)
      else avisos.erro('Não foi possível salvar', erro.message)
    },
  })

  if (consulta.isPending) return <EstadoCarregando />
  if (consulta.isError || !lead) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }

  return (
    <>
      <CabecalhoPagina
        titulo={`Editar ${lead.cliente.nome}`}
        descricao="Somente dados cadastrais. Toda alteração entra na auditoria."
        acoes={
          <Botao
            variante="contorno"
            onClick={() => navegar(`/leads/${uuid}`)}
            iconeEsquerda={<IconeVoltar className="h-4 w-4" />}
          >
            Voltar ao atendimento
          </Botao>
        }
      />

      <div className="flex flex-col gap-5">
        <Alerta tom="info">
          Situação, responsável, equipe e unidade não são editáveis aqui: mudança de situação exige
          registro de interação e troca de responsável exige motivo de transferência — as duas
          precisam aparecer na timeline.
        </Alerta>

        {/* --- Classificação do lead --- */}
        <Cartao semPadding>
          <CabecalhoCartao
            titulo="Classificação do atendimento"
            descricao="Dimensões usadas nos relatórios."
          />
          <CorpoCartao className="grid gap-4 sm:grid-cols-3">
            <Campo rotulo="Ponto de conversão">
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Não informado"
                  value={classificacao.pontoConversaoId}
                  opcoes={opcoesDeCatalogo(pontos.data?.dados)}
                  onChange={(evento) =>
                    setClassificacao((atual) => ({
                      ...atual,
                      pontoConversaoId: evento.target.value,
                    }))
                  }
                />
              )}
            </Campo>

            <Campo rotulo="Produto associado">
              {(atributos) => (
                <Selecao
                  {...atributos}
                  vazio="Não informado"
                  value={classificacao.produtoAssociadoId}
                  opcoes={opcoesDeCatalogo(produtos.data?.dados)}
                  onChange={(evento) =>
                    setClassificacao((atual) => ({
                      ...atual,
                      produtoAssociadoId: evento.target.value,
                    }))
                  }
                />
              )}
            </Campo>

            <Campo rotulo="Valor do negócio" dica="Formato 350000.00.">
              {(atributos) => (
                <Entrada
                  {...atributos}
                  inputMode="decimal"
                  placeholder="350000.00"
                  value={classificacao.valorNegocio}
                  onChange={(evento) =>
                    setClassificacao((atual) => ({ ...atual, valorNegocio: evento.target.value }))
                  }
                />
              )}
            </Campo>
          </CorpoCartao>
          <RodapeCartao>
            <Botao
              onClick={() => salvarClassificacao.mutate()}
              carregando={salvarClassificacao.isPending}
            >
              Salvar classificação
            </Botao>
          </RodapeCartao>
        </Cartao>

        {/* --- Cadastro do cliente --- */}
        <Cartao semPadding>
          <CabecalhoCartao
            titulo="Cadastro do cliente"
            descricao="Vale para todos os atendimentos desta pessoa."
          />
          <CorpoCartao className="flex flex-col gap-4">
            {erroCliente?.naoEncontrado ? (
              <EstadoIndisponivel
                recurso="PATCH /api/v1/clientes/{uuid}"
                descricao="A edição de cadastro do cliente está prevista no contrato (§5) mas a rota ainda não foi publicada. Os campos abaixo já estão prontos para ela."
              />
            ) : erroCliente ? (
              <Alerta tom="perigo">{erroCliente.message}</Alerta>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Nome" obrigatorio>
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.nome}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, nome: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              <Campo rotulo="E-mail">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    type="email"
                    value={cadastro.email}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, email: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              <Campo rotulo="CPF / CNPJ">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    inputMode="numeric"
                    value={cadastro.documento}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, documento: evento.target.value }))
                    }
                  />
                )}
              </Campo>

              <Campo
                rotulo="Contato principal"
                dica="Alterar o telefone principal muda a chave de deduplicação — peça ao administrador."
              >
                {(atributos) => (
                  <Entrada {...atributos} disabled value={lead.cliente.contatoPrincipal} />
                )}
              </Campo>
            </div>

            <fieldset className="grid gap-4 rounded-md border border-borda p-3 sm:grid-cols-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-conteudo-fraco">
                Endereço
              </legend>
              <Campo rotulo="CEP">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.cep}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, cep: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Logradouro" className="sm:col-span-2">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.logradouro}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, logradouro: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Bairro">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.bairro}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, bairro: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Cidade">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.cidade}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, cidade: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="UF">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    maxLength={2}
                    value={cadastro.estado}
                    onChange={(evento) =>
                      setCadastro((atual) => ({
                        ...atual,
                        estado: evento.target.value.toUpperCase(),
                      }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Complemento" className="sm:col-span-3">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.complemento}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, complemento: evento.target.value }))
                    }
                  />
                )}
              </Campo>
            </fieldset>

            <fieldset className="grid gap-4 rounded-md border border-borda p-3 sm:grid-cols-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-conteudo-fraco">
                Dados profissionais
              </legend>
              <Campo rotulo="Cargo">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.cargo}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, cargo: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Empresa">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.empresa}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, empresa: evento.target.value }))
                    }
                  />
                )}
              </Campo>
              <Campo rotulo="Site">
                {(atributos) => (
                  <Entrada
                    {...atributos}
                    value={cadastro.site}
                    onChange={(evento) =>
                      setCadastro((atual) => ({ ...atual, site: evento.target.value }))
                    }
                  />
                )}
              </Campo>
            </fieldset>
          </CorpoCartao>
          <RodapeCartao>
            <Botao onClick={() => salvarCliente.mutate()} carregando={salvarCliente.isPending}>
              Salvar cadastro
            </Botao>
          </RodapeCartao>
        </Cartao>
      </div>
    </>
  )
}
